import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Loader2, Search, Building2, UserPlus, X } from 'lucide-react';
import { toast } from 'sonner';

const emptyForm = { name: '', email: '', phone: '', password: '' };

export const CreateClientModal = ({ open, onClose, api, onCreated }) => {
  const [form, setForm] = useState(emptyForm);
  const [orgQuery, setOrgQuery] = useState('');
  const [orgSearching, setOrgSearching] = useState(false);
  const [orgResults, setOrgResults] = useState([]);
  const [selectedOrg, setSelectedOrg] = useState(null); // { id, name }
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) {
      setForm(emptyForm);
      setOrgQuery(''); setOrgResults([]); setSelectedOrg(null);
    }
  }, [open]);

  useEffect(() => {
    if (!open || selectedOrg) return;
    const q = orgQuery.trim();
    if (q.length < 2) { setOrgResults([]); return; }
    const handle = setTimeout(async () => {
      setOrgSearching(true);
      try {
        const res = await api.get('/day-pass-packages/client-search', { params: { q } });
        setOrgResults(res.data?.organizations || []);
      } catch (e) {
        // silent — typeahead, not critical
      } finally {
        setOrgSearching(false);
      }
    }, 300);
    return () => clearTimeout(handle);
  }, [orgQuery, open, selectedOrg, api]);

  const submit = async () => {
    if (!form.name.trim() || !form.email.trim() || !form.password) {
      toast.error('Name, email, and password are required');
      return;
    }
    setSubmitting(true);
    try {
      const res = await api.post('/users/create', {
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone || undefined,
        password: form.password,
        role: 'client',
        organization_id: selectedOrg?.id || undefined,
        user_type: selectedOrg ? 'b2b' : 'b2c',
      });
      toast.success(res.data?.converted_from_guest
        ? `Guest ${form.name} converted to a client — past bookings kept`
        : `Client ${form.name} created`);
      onCreated?.(res.data);
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to create client');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto" data-testid="sales-create-client-modal">
        <DialogHeader>
          <DialogTitle>Create Client</DialogTitle>
        </DialogHeader>

        <div className="space-y-3 py-2">
          <div>
            <Label>Name *</Label>
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="create-client-name" />
          </div>
          <div>
            <Label>Email *</Label>
            <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="create-client-email" />
          </div>
          <div>
            <Label>Phone</Label>
            <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="create-client-phone" />
          </div>
          <div>
            <Label>Password *</Label>
            <Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="create-client-password" />
          </div>

          <div>
            <Label>Organization (optional — for B2B clients)</Label>
            {selectedOrg ? (
              <div className="flex items-center justify-between p-2.5 rounded-lg bg-muted/30 border border-border mt-1">
                <div className="flex items-center gap-2 min-w-0">
                  <Building2 className="w-4 h-4 text-primary shrink-0" />
                  <p className="text-sm font-medium truncate">{selectedOrg.name}</p>
                </div>
                <Button type="button" size="icon" variant="ghost" className="h-6 w-6 shrink-0" onClick={() => setSelectedOrg(null)}>
                  <X className="w-3.5 h-3.5" />
                </Button>
              </div>
            ) : (
              <div className="relative mt-1">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input
                  className="pl-9"
                  placeholder="Search organization…"
                  value={orgQuery}
                  onChange={(e) => setOrgQuery(e.target.value)}
                  data-testid="create-client-org-search"
                />
                {orgSearching && <Loader2 className="w-4 h-4 absolute right-3 top-1/2 -translate-y-1/2 animate-spin text-muted-foreground" />}
                {orgResults.length > 0 && (
                  <div className="absolute z-10 mt-1 w-full max-h-56 overflow-y-auto rounded-lg border border-border bg-popover shadow-md">
                    {orgResults.map((o) => (
                      <button
                        key={o.id}
                        type="button"
                        className="w-full text-left px-3 py-2 text-sm hover:bg-muted/50 flex items-center gap-2"
                        onClick={() => { setSelectedOrg({ id: o.id, name: o.name }); setOrgQuery(''); }}
                      >
                        <Building2 className="w-3.5 h-3.5 text-primary" /> {o.name}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
            <p className="text-xs text-muted-foreground mt-1">Leave blank for a B2C (individual) client.</p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={submitting} data-testid="create-client-submit">
            {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <UserPlus className="w-4 h-4 mr-2" />}
            Create Client
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CreateClientModal;
